import React from 'react';
import {
  Typography,
  Box,
  Fab,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CloudUploadRounded,
  CheckCircleOutlineRounded,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MobileDateTimePicker } from '@mui/x-date-pickers';

import { BootstrapTooltip } from '../StyledComponents';
import { documentsType, assessmentsType } from '../constants';

const PatientUploadForm = ({ hook, isStudentUser = false }) => {
  const {
    uploadedFile,
    isLoading,
    handleFileChange,
    date,
    dateChangeHandler,
    typeOfDocument,
    setTypeOfDocument,
    setOtherDocumentType,
    typeOfAssessment,
    setTypeOfAssessment,
    setOtherAssessmentType,
    information,
    setInformation,
    error,
    handleClose,
    attachmentSaveHandler,
    extractDocumentsFromGoals,
    setExtractDocumentsFromGoals,
   
  } = hook;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 1,
      }}
    >
      <Box className='fullWidth flexBox flexColumn flexCenter'>
        <Typography
          gutterBottom
          sx={{
            textAlign: 'center',
            mt: '1rem',
            fontFamily: 'Lato',
          }}
        >
          Upload attachment for review
        </Typography>
        <Box className='flexBox flexCenter'>
          <BootstrapTooltip title='choose a file' placement='right'>
            <Fab
              variant='extended'
              component='label'
              color='secondary'
              size='small'
            >
              <CloudUploadRounded />
              <input
                hidden
                accept='.pdf'
                multiple
                type='file'
                onChange={handleFileChange}
                disabled={isLoading}
              />
            </Fab>
          </BootstrapTooltip>
        </Box>
        <Typography className='flexBox flexCenter' color={'GrayText'}>
          {uploadedFile?.name && (
            <>
              {uploadedFile?.name}
              <CheckCircleOutlineRounded color='success' />
            </>
          )}
        </Typography>
      </Box>

      <Typography gutterBottom>* Date of attachment created:</Typography>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MobileDateTimePicker
          label='Date of Attachment Created'
          disableFuture
          className='fullWidth'
          value={date || null}
          onChange={dateChangeHandler}
          disabled={isLoading}
          renderInput={(params) => <TextField {...params} />}
        />
      </LocalizationProvider>

      <Typography gutterBottom>*Type of Document</Typography>

      <Select
        value={typeOfDocument}
        className='fullWidth'
        onChange={(e) => setTypeOfDocument(e.target.value)}
        disabled={isLoading}
      >
        {documentsType?.map((data) => (
          <MenuItem key={data.value} value={data.value}>
            {data.label}
          </MenuItem>
        ))}
      </Select>
      {typeOfDocument === 'Other' && (
        <TextField
          variant='outlined'
          size='medium'
          label='Specify document type'
          fullWidth
          onChange={(e) => setOtherDocumentType(e.target.value)}
          disabled={isLoading}
          sx={{ mt: 2 }}
        />
      )}

      {typeOfDocument === 'Assessment' && (
        <>
          <Typography variant='subtitle1' fontWeight='medium' gutterBottom>
            * Type of Assessment
          </Typography>
          <Select
            value={typeOfAssessment}
            fullWidth
            onChange={(e) => setTypeOfAssessment(e.target.value)}
            disabled={isLoading}
          >
            {assessmentsType.map((data) => (
              <MenuItem key={data.value} value={data.value}>
                {data.label}
              </MenuItem>
            ))}
          </Select>

          {typeOfAssessment === 'Other' && (
            <TextField
              variant='outlined'
              size='medium'
              label='Specify assessment type'
              fullWidth
              onChange={(e) => setOtherAssessmentType(e.target.value)}
              disabled={isLoading}
              sx={{ mt: 2 }}
            />
          )}
        </>
      )}

      {isStudentUser && typeOfDocument !== 'Assessment' && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={extractDocumentsFromGoals}
                onChange={(e) => setExtractDocumentsFromGoals(e.target.checked)}
              />
            }
            label='Extract goals from document content'
          />
        </Box>
      )}

      {/* {isStudentUser && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={extractOtherInfo}
                onChange={(e) => setExtractOtherInfo(e.target.checked)}
              />
            }
            label='Extract additional information from document'
          />
        </Box>
      )} */}

      <Typography gutterBottom>Notes:</Typography>
      <TextField
        className='notes fullWidth fullHeight'
        variant='outlined'
        size='medium'
        label='Please input notes...'
        sx={{ my: 0.5 }}
        multiline
        rows={8}
        onChange={(e) =>
          setInformation({ ...information, notes: e.target.value })
        }
        disabled={isLoading}
      />
      {error.show && (
        <Typography color={'red'} sx={{ py: 2 }}>
          {error.message}
        </Typography>
      )}

      <Box className='fullWidth flexBox flexCenter flexWrap' gap={2} mt={3}>
        <Button
          variant='outlined'
          onClick={handleClose}
          sx={{
            width: '30%',
            fontFamily: 'Lato',
            textTransform: 'capitalize',
            color: 'var(--clr-theme-purple)',
            borderColor: 'var(--clr-theme-purple)',
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant='extended'
          onClick={attachmentSaveHandler}
          sx={{
            width: '30%',
            backgroundColor: 'var(--clr-theme-purple)!important',
            color: 'var(--clr-theme-white)',
            '&:disabled': {
              color: 'var(--clr-theme-white) !important',
              backgroundColor: 'rgb(211, 211, 211) !important',
            },
          }}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : null
          }
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default PatientUploadForm;
